import { useState, useEffect, useRef } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { Plus, Pencil, Trash2, Upload, X, ImageIcon, Link2, Smartphone } from 'lucide-react'
import { Modal, ConfirmModal } from '../../common/Modal/Modal'
import {
  fetchCollections,
  saveCollection,
  deleteCollection,
  clearCollectionError,
} from './services/collectionSlice'

const EMPTY_FORM = { title: '', link: '', status: 1, image: null, mobile_image: null, remove_mobile: false }

// natural size check (the backend validates this too)
const readSize = (file) =>
  new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file)
    const img = new Image()
    img.onload = () => { resolve({ w: img.naturalWidth, h: img.naturalHeight }); URL.revokeObjectURL(url) }
    img.onerror = () => { reject(new Error('Invalid image')); URL.revokeObjectURL(url) }
    img.src = url
  })

export default function Collection() {
  const dispatch = useDispatch()
  const { list, slots, isLoading, isSaving, isError, message, errors } =
    useSelector((state) => state.collection)

  const [open, setOpen] = useState(false)
  const [deleteOpen, setDeleteOpen] = useState(false)
  const [position, setPosition] = useState(1)
  const [form, setForm] = useState(EMPTY_FORM)
  const [localError, setLocalError] = useState('')
  const [imgPreview, setImgPreview] = useState(null)
  const [mobPreview, setMobPreview] = useState(null)
  const imgRef = useRef(null)
  const mobRef = useRef(null)

  useEffect(() => { dispatch(fetchCollections()) }, [dispatch])

  const byPos = (p) => list.find((x) => x.position === p)
  const slot = slots[position] || { label: '', width: 0, height: 0 }
  const current = byPos(position)

  const revoke = () => {
    if (imgPreview) URL.revokeObjectURL(imgPreview)
    if (mobPreview) URL.revokeObjectURL(mobPreview)
  }

  const openSlot = (p) => {
    dispatch(clearCollectionError())
    revoke()
    const c = byPos(p)
    setPosition(p)
    setForm(c
      ? { ...EMPTY_FORM, title: c.title || '', link: c.link || '', status: c.status }
      : EMPTY_FORM)
    setImgPreview(null)
    setMobPreview(null)
    setLocalError('')
    setOpen(true)
  }

  const pickImage = async (e) => {
    const file = e.target.files?.[0]
    e.target.value = ''
    if (!file) return
    setLocalError('')
    try {
      const { w, h } = await readSize(file)
      if (w !== slot.width || h !== slot.height) {
        setLocalError(`Image size must be exactly ${slot.width} x ${slot.height} px. Your image is ${w} x ${h} px.`)
        return
      }
    } catch {
      setLocalError('Please select a valid image.')
      return
    }
    if (imgPreview) URL.revokeObjectURL(imgPreview)
    setImgPreview(URL.createObjectURL(file))
    setForm((f) => ({ ...f, image: file }))
  }

  const pickMobile = (e) => {
    const file = e.target.files?.[0]
    e.target.value = ''
    if (!file) return
    if (mobPreview) URL.revokeObjectURL(mobPreview)
    setMobPreview(URL.createObjectURL(file))
    setForm((f) => ({ ...f, mobile_image: file, remove_mobile: false }))
  }

  const save = async () => {
    setLocalError('')
    if (!current && !form.image) {
      setLocalError('Image upload is required.')
      return
    }
    const fd = new FormData()
    fd.append('position', position)
    fd.append('title', form.title || '')
    fd.append('link', form.link || '')
    fd.append('status', form.status ? 1 : 0)
    if (form.image) fd.append('image', form.image)
    if (form.mobile_image) fd.append('mobile_image', form.mobile_image)
    if (form.remove_mobile && !form.mobile_image) fd.append('remove_mobile', 1)

    const result = await dispatch(saveCollection(fd))
    if (result.meta.requestStatus === 'fulfilled') { revoke(); setOpen(false) }
  }

  const onDelete = async () => {
    const result = await dispatch(deleteCollection(position))
    if (result.meta.requestStatus === 'fulfilled') { setDeleteOpen(false); setOpen(false) }
  }

  const fieldError = (name) =>
    errors?.[name] ? <small style={{ color: '#ef4444' }}>{errors[name][0]}</small> : null

  const shownImage = imgPreview || current?.image
  const existingMobile = !form.remove_mobile ? current?.mobile_image : null
  const shownMobile = mobPreview || existingMobile

  /* ---- one tile of the layout preview ---- */
  const Tile = ({ p, cls }) => {
    const c = byPos(p)
    return (
      <button type="button" className={`col-tile ${cls || ''}`} style={{ aspectRatio: `${slots[p]?.width || 1} / ${slots[p]?.height || 1}` }} onClick={() => openSlot(p)}>
        {c?.image ? <img src={c.image} alt={c.title || `Slot ${p}`} /> : (
          <span className="col-empty"><ImageIcon size={22} /><b>Slot {p}</b>
            <small>{slots[p]?.width} x {slots[p]?.height}</small></span>
        )}
        <span className="col-tag">{p}. {slots[p]?.label}</span>
        {c && c.status !== 1 && <span className="col-off">Hidden</span>}
        <span className="col-hover">{c ? <><Pencil size={14} /> Edit</> : <><Plus size={14} /> Add image</>}</span>
      </button>
    )
  }

  return (
    <div className="dash-page page-in">
      <div className="pg-header">
        <div>
          <h1 className="page-title" style={{ margin: 0 }}>Home Collections</h1>
          <p style={{ margin: '4px 0 0', color: 'var(--text3)', fontSize: 13 }}>
            “Shop Our Most-Loved Collections” — 4 fixed slots. Click a slot to set its image, title and link.
          </p>
        </div>
      </div>

      {isError && message && !open && <div className="login-error">{message}</div>}

      <div className="card" style={{ padding: 'var(--space-lg)' }}>
        <div className="col-grid">
          <Tile p={1} cls="tall" />
          <div className="col-stack">
            <Tile p={2} cls="wide" />
            <Tile p={3} cls="wide" />
          </div>
          <Tile p={4} cls="tall" />
        </div>
        {isLoading && <p style={{ textAlign: 'center', color: 'var(--text3)', marginTop: 12 }}>Loading…</p>}
      </div>

      {/* ---------- EDIT MODAL ---------- */}
      <Modal
        open={open}
        title={`Slot ${position} — ${slot.label}`}
        subtitle={`Required size: ${slot.width} x ${slot.height} px (jpg / png / webp, max 5MB)`}
        onClose={() => setOpen(false)}
        footer={(
          <>
            {current && (
              <button className="btn btn-outline" style={{ marginRight: 'auto', color: '#ef4444' }}
                onClick={() => setDeleteOpen(true)}>
                <Trash2 size={14} /> Remove
              </button>
            )}
            <button className="btn btn-outline" onClick={() => setOpen(false)}>Cancel</button>
            <button className="btn btn-primary" onClick={save} disabled={isSaving}>
              {isSaving ? 'Saving…' : 'Save'}
            </button>
          </>
        )}
      >
        {(localError || (isError && message)) && (
          <div className="login-error" style={{ marginBottom: 12 }}>{localError || message}</div>
        )}

        <div className="form-grid cols-2">
          <div className="form-field full">
            <label>Image <span style={{ color: '#ef4444' }}>*</span> <small style={{ color: 'var(--text3)' }}>({slot.width} x {slot.height})</small></label>
            <div className="col-drop" style={{ aspectRatio: `${slot.width} / ${slot.height}` }}
              onClick={() => imgRef.current?.click()}>
              {shownImage ? <img src={shownImage} alt="" /> : (
                <span><Upload size={22} /><br />Click to upload</span>
              )}
            </div>
            <input ref={imgRef} type="file" accept="image/png,image/jpeg,image/webp" hidden onChange={pickImage} />
            {fieldError('image')}
          </div>

          <div className="form-field">
            <label>Title (alt text)</label>
            <input className="input" value={form.title} placeholder="e.g. Passport Covers"
              onChange={(e) => setForm({ ...form, title: e.target.value })} />
            {fieldError('title')}
          </div>
          <div className="form-field">
            <label><Link2 size={12} /> Link</label>
            <input className="input" value={form.link} placeholder="/shop/passport-cover"
              onChange={(e) => setForm({ ...form, link: e.target.value })} />
            {fieldError('link')}
          </div>

          <div className="form-field full">
            <label><Smartphone size={12} /> Mobile image <small style={{ color: 'var(--text3)' }}>(optional — if empty, the desktop image is shown)</small></label>
            <div className="col-mob">
              {shownMobile && <img src={shownMobile} alt="" />}
              <button type="button" className="btn btn-outline btn-sm" onClick={() => mobRef.current?.click()}>
                <Upload size={13} /> {shownMobile ? 'Replace' : 'Upload'}
              </button>
              {shownMobile && (
                <button type="button" className="btn btn-ghost btn-sm" onClick={() => {
                  if (mobPreview) URL.revokeObjectURL(mobPreview)
                  setMobPreview(null)
                  setForm({ ...form, mobile_image: null, remove_mobile: !!current?.mobile_image })
                }}><X size={13} /> Remove</button>
              )}
            </div>
            <input ref={mobRef} type="file" accept="image/png,image/jpeg,image/webp" hidden onChange={pickMobile} />
            {fieldError('mobile_image')}
          </div>

          <div className="form-field full">
            <label className="col-check">
              <input type="checkbox" checked={!!form.status}
                onChange={(e) => setForm({ ...form, status: e.target.checked ? 1 : 0 })} />
              Active (show on website)
            </label>
          </div>
        </div>
      </Modal>

      <ConfirmModal
        open={deleteOpen}
        title="Remove image"
        message={`Remove the image from slot ${position}? The default static image will show on the website.`}
        onClose={() => setDeleteOpen(false)}
        onConfirm={onDelete}
      />

      <style>{`
        .dash-page { display: flex; flex-direction: column; gap: var(--space-lg); width: 100%; }
        .pg-header { display: flex; align-items: center; justify-content: space-between; gap: var(--space-md); flex-wrap: wrap; }
        .login-error {
          background: rgba(239,68,68,.12); border: 1px solid rgba(239,68,68,.35);
          color: #fca5a5; font-size: .875rem; padding: .6rem .9rem; border-radius: 10px;
        }

        /* same layout as website */
        /* column widths = slot ratios, jethi 3 columns ni height barabar rahe */
        .col-grid { display: grid; grid-template-columns: 0.8fr 0.926fr 1fr; gap: 12px; align-items: start; }
        .col-stack { display: grid; gap: 12px; }
        .col-tile {
          position: relative; width: 100%; padding: 0; border: 1.5px dashed var(--border);
          border-radius: 12px; overflow: hidden; cursor: pointer; background: var(--bg-hover, #f4f5fa);
          display: block;
        }
        .col-tile img { width: 100%; height: 100%; object-fit: cover; display: block; }
        .col-empty {
          position: absolute; inset: 0; display: flex; flex-direction: column;
          align-items: center; justify-content: center; gap: 4px; color: var(--text3);
        }
        .col-tag {
          position: absolute; left: 8px; top: 8px; padding: 3px 9px; border-radius: 999px;
          font-size: 11px; font-weight: 600; background: rgba(0,0,0,.6); color: #fff;
        }
        .col-off {
          position: absolute; right: 8px; top: 8px; padding: 3px 9px; border-radius: 999px;
          font-size: 11px; background: #ef4444; color: #fff;
        }
        .col-hover {
          position: absolute; inset: 0; display: flex; align-items: center; justify-content: center; gap: 6px;
          background: rgba(79,70,229,.55); color: #fff; font-weight: 600; opacity: 0; transition: opacity .18s ease;
        }
        .col-tile:hover .col-hover { opacity: 1; }
        @media (max-width: 700px) {
          .col-grid { grid-template-columns: 1fr; }
        }

        .col-drop {
          width: 100%; max-height: 360px; border: 1.5px dashed var(--border); border-radius: 12px;
          display: grid; place-items: center; text-align: center; cursor: pointer; overflow: hidden;
          color: var(--text3); background: var(--bg-hover, #f4f5fa); margin: 0 auto;
        }
        .col-drop:hover { border-color: var(--accent, #4F46E5); }
        .col-drop img { width: 100%; height: 100%; object-fit: cover; }
        .col-mob { display: flex; align-items: center; gap: 10px; flex-wrap: wrap; }
        .col-mob img { width: 56px; height: 72px; object-fit: cover; border-radius: 8px; border: 1px solid var(--border); }
        .col-check { display: inline-flex; align-items: center; gap: 8px; cursor: pointer; }
      `}</style>
    </div>
  )
}